import { defineComponent as _defineComponent } from 'vue'
import { queries, mutations } from "@/apollo";
import { User, PaymentType } from "@/CodegenTypes";

import PaymentStatementModal from "@/components/modules/profile/PaymentStatementModal.vue";

import { sumBy, sortBy } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";
import { errorMutationNotification } from "@/utils";
import { PAYMENT_TYPE_DISPLAY } from "@/constants";
import { fixRounding } from "@/helpers/fixRounding";
import { isBelowTolerance } from "@/helpers/isBelowTolerance";
import { addSuccessToast } from "@/vue-service";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import { useQuery } from "@vue/apollo-composable";
import { apolloClient } from "@/apollo";
import { stringRouteQuery } from "@/helpers/stringRouteQuery";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentCollection',
  setup(__props) {

const route = useRoute();
const userId = computed(() => stringRouteQuery(route.query.userId));

const { result, loading, refetch } = useQuery<{ user: User }>(
  queries.userWithDownlinePaymentPayableSum,
  {
    userId,
  }
);

const selectedUserId = ref<null | string>(null);
const hideForm = ref(false);
const showPaymentStatementModal = ref(false);

const submitting = ref(false);

const { $t } = useTranslation();


const form = ref<
  {
    userId: string;
    amount: number;
    paymentType: PaymentType;
    remark: string;
    checked: boolean;
    enabled: boolean;
  }[]
>([]);

const checkedDownlines = computed(() => {
  return form.value.filter((r) => r.checked && r.enabled);
});

const processedDownlines = computed(() => {
  const downlines = result.value?.user.downlines.map((dl) => {
    const amountOutstanding = dl.paymentPayableSum;

    return {
      ...dl,
      amountOutstanding: isBelowTolerance(amountOutstanding)
        ? 0
        : amountOutstanding,
    };
  });

  return sortBy(downlines, (dl) => dl.userId);
});

const allChecked = computed({
  get() {
    if (!form.value) {
      return false;
    }
    return (
      checkedDownlines.value.length ===
      form.value.filter((f) => f.enabled).length
    );
  },
  set(val: boolean) {
    if (!form.value) {
      return;
    }
    if (allChecked.value) {
      for (const dl of form.value) {
        dl.checked = false;
      }
    } else {
      for (const dl of form.value) {
        dl.checked = true;
      }
    }
  },
});

const totalOutstanding = computed(() => {
  return sumBy(processedDownlines.value, (dl) => dl.amountOutstanding);
});

const descriptionOptions = computed(() => {
  return Object.entries(PAYMENT_TYPE_DISPLAY)
    .filter(([key, val]) => !val.isSystemType)
    .map(([key, val]) => ({
      label: val.display,
      value: key,
    }));
});

watch(processedDownlines, (val) => {
  refillForm();
});

function refillForm() {
  if (!processedDownlines.value) {
    return;
  }
  form.value = [];
  for (const downline of processedDownlines.value) {
    const amount = downline.amountOutstanding;
    const paymentType =
      amount < 0 ? PaymentType.PaymentReceived : PaymentType.StrikePayout;
    form.value.push({
      userId: downline.userId,
      paymentType,
      amount: +Math.abs(amount).toFixed(2),
      remark: "",
      checked: false,
      enabled: downline.autoResetBalance === false,
    });
  }
}

async function updatePayment() {
  if (!form.value) {
    return;
  }
  try {
    submitting.value = true;
    await apolloClient.mutate({
      mutation: mutations.updatePayment,
      variables: {
        payload: {
          payments: form.value
            .filter((r) => r.checked && r.enabled)
            .map((r) => ({
              payorUserId: r.userId,
              amount: r.amount,
              paymentType: r.paymentType,
              remark: r.remark,
            })),
        },
      },
    });
    addSuccessToast("Payment updated.");
    reloadAll();
  } catch (err) {
    errorMutationNotification(err);
  }
  submitting.value = false;
}

onMounted(() => {
  refillForm();
});

function reloadAll() {
  refetch();
}

function openPaymentStatementModal(userId: User["userId"]) {
  showPaymentStatementModal.value = true;
  selectedUserId.value = userId;
}

async function handleCopy(event?: Event, row?: number) {
  const downlines =
    row !== undefined
      ? [processedDownlines.value[row]]
      : processedDownlines.value;

  const data = downlines
    .map((dl) => {
      let result = ``;

      result = [
        dl.userId.toUpperCase(),
        dl.username,
        formatDollar(dl.creditGiven),
        formatDollar(dl.creditSummary.creditGivenToDownlines),
        formatDollar(dl.creditSummary.betsInProgress),
        formatDollar(dl.creditSummary.creditBalance),
        dl.autoResetBalance ? "Auto" : "Manual",
        formatDollar(dl.amountOutstanding),
      ].join("\t");

      return result;
    })
    .join("\n");

  await window.navigator.clipboard.writeText(data);
}

return { __sfc: true,route, userId, result, loading, refetch, selectedUserId, hideForm, showPaymentStatementModal, submitting, $t, form, checkedDownlines, processedDownlines, allChecked, totalOutstanding, descriptionOptions, refillForm, updatePayment, reloadAll, openPaymentStatementModal, handleCopy, PaymentType, PaymentStatementModal, formatDollar, fixRounding }
}

})